import { GalleryImage } from './types'
import {
  SupabaseRequestError,
  encodeStoragePath,
  getPublicStorageUrl,
  supabaseFetch,
} from './supabase'

const GALLERY_BUCKET = 'gallery'

interface GalleryImageRow {
  id: string
  storage_path: string
  caption: string | null
  created_at: string
}

export const loadRemoteGalleryImages = async (): Promise<GalleryImage[]> => {
  const response = await supabaseFetch(
    '/rest/v1/gallery_images?select=id,storage_path,caption,created_at&order=created_at.desc',
  )
  const rows = (await response.json()) as GalleryImageRow[]
  return rows.map(mapGalleryRow)
}

export const uploadRemoteGalleryImage = async (
  file: File,
  caption?: string,
): Promise<GalleryImage> => {
  const storagePath = createStoragePath(file)
  await supabaseFetch(
    `/storage/v1/object/${GALLERY_BUCKET}/${encodeStoragePath(storagePath)}`,
    {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'x-upsert': 'false',
      },
      body: file,
    },
    { requireAuth: true },
  )

  try {
    const response = await supabaseFetch(
      '/rest/v1/gallery_images',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Prefer: 'return=representation',
        },
        body: JSON.stringify({
          storage_path: storagePath,
          caption: caption?.trim() || null,
        }),
      },
      { requireAuth: true },
    )
    const rows = (await response.json()) as GalleryImageRow[]
    if (!rows[0])
      throw new Error('Slika je prenesena, ali zapis galerije nije vraćen.')
    return mapGalleryRow(rows[0])
  } catch (error) {
    await removeStorageObject(storagePath).catch(() => undefined)
    throw error
  }
}

export const deleteRemoteGalleryImage = async (
  image: GalleryImage,
): Promise<void> => {
  if (!image.storagePath)
    throw new Error('Nedostaje putanja datoteke za ovu sliku.')
  await supabaseFetch(
    `/rest/v1/gallery_images?id=eq.${encodeURIComponent(image.id)}`,
    { method: 'DELETE' },
    { requireAuth: true },
  )
  await removeStorageObject(image.storagePath)
}

export const getGalleryErrorMessage = (error: unknown): string => {
  if (error instanceof SupabaseRequestError) {
    const details = error.message.toLowerCase()
    if (error.status === 401)
      return 'Za upravljanje galerijom potrebna je administratorska prijava.'
    if (
      error.status === 404 ||
      details.includes('gallery_images') ||
      details.includes('bucket')
    ) {
      return 'Supabase galerija još nije postavljena. U Supabase SQL Editoru pokrenite skriptu iz SUPABASE_SETUP.md.'
    }
    if (error.status === 403)
      return 'Supabase ne dopušta ovu radnju. Provjerite RLS pravila iz SUPABASE_SETUP.md.'
    return `Galerija se ne može povezati sa Supabaseom: ${error.message}`
  }
  return error instanceof Error
    ? error.message
    : 'Dogodila se neočekivana greška pri radu s galerijom.'
}

const mapGalleryRow = (row: GalleryImageRow): GalleryImage => ({
  id: row.id,
  storagePath: row.storage_path,
  url: getPublicStorageUrl(GALLERY_BUCKET, row.storage_path),
  caption: row.caption || undefined,
  createdAt: row.created_at,
})

const removeStorageObject = async (storagePath: string): Promise<void> => {
  await supabaseFetch(
    `/storage/v1/object/${GALLERY_BUCKET}/${encodeStoragePath(storagePath)}`,
    { method: 'DELETE' },
    { requireAuth: true },
  )
}

const createStoragePath = (file: File): string =>
  `uploads/${new Date().toISOString().slice(0, 10)}/${createId()}.${getFileExtension(file)}`

const createId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`

const getFileExtension = (file: File): string => {
  const fromName = file.name.split('.').pop()?.toLowerCase()
  if (fromName && /^[a-z0-9]{1,10}$/.test(fromName)) return fromName
  const fromType = file.type.split('/')[1]?.toLowerCase()
  return fromType && /^[a-z0-9]{1,10}$/.test(fromType) ? fromType : 'jpg'
}
